import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertOctagon, ArrowLeftRight, BadgeEuro, FileCheck, ListTree, Scale, TrendingUp } from "lucide-react";
import api, { apiError, ruleReasons } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CERT_STATUS, CHANGE_STATUS, FORECAST_V_STATUS, LEDGER_SOURCE, LEDGER_STAGE, fmtDate, fmtEUR } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ReasonsList } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "resumen", label: "Resumen económico", icon: BadgeEuro },
  { id: "estructura", label: "Estructura de costes", icon: ListTree },
  { id: "movimientos", label: "Ledger", icon: ArrowLeftRight },
  { id: "cambios", label: "Cambios", icon: Scale },
  { id: "forecast", label: "Forecast", icon: TrendingUp },
  { id: "certificaciones", label: "Certificaciones", icon: FileCheck },
  { id: "progreso", label: "Avance físico vs financiero", icon: AlertOctagon },
];

const COST_CODE_NAMES = {
  "01": "Gestión de proyecto", "02": "Diseño e ingeniería", "03": "Licencias y permisos",
  "04": "Contratación y legal", "05": "Obra", "05.1": "Cimentación y estructura",
  "05.2": "Cerramiento y envolvente", "05.3": "Instalaciones", "05.4": "Acabados",
  "06": "Puesta en marcha", "07": "Entrega y cierre",
};

// ---------------- RESUMEN CON DRILL-DOWN ----------------

function ResumenTab({ pid }) {
  const { data: e } = useQuery({ queryKey: ["econ", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia`)).data });
  if (!e) return null;
  const figures = [
    { key: "original_budget", label: "Original Budget", value: e.original_budget, drill: "movimientos", params: "stage=presupuesto&source_type=budget_line", testid: "econ-original-budget" },
    { key: "approved_changes", label: "Approved Changes", value: e.approved_changes, drill: "cambios", params: "", testid: "econ-approved-changes" },
    { key: "current_budget", label: "Current Budget", value: e.current_budget, drill: "estructura", params: "", testid: "econ-current-budget" },
    { key: "reserved", label: "Reserved", value: e.reserved, drill: "movimientos", params: "stage=presupuesto", testid: "econ-reserved" },
    { key: "committed", label: "Committed", value: e.committed, drill: "movimientos", params: "stage=compromiso", testid: "econ-committed" },
    { key: "certified", label: "Certified", value: e.certified, drill: "movimientos", params: "stage=certificado", testid: "econ-certified" },
    { key: "invoiced", label: "Invoiced", value: e.invoiced, drill: "movimientos", params: "stage=facturado", testid: "econ-invoiced" },
    { key: "paid", label: "Paid", value: e.paid, drill: "movimientos", params: "stage=pagado", testid: "econ-paid" },
    { key: "forecast_to_complete", label: "Forecast to Complete", value: e.forecast_to_complete, drill: "forecast", params: "", testid: "econ-ftc" },
    { key: "forecast_final_cost", label: "Forecast Final Cost", value: e.forecast_final_cost, drill: "forecast", params: "", testid: "econ-ffc" },
    { key: "variance", label: "Variance", value: e.variance, drill: "estructura", params: "", testid: "econ-variance" },
  ];
  return (
    <div className="space-y-4" data-testid="econ-resumen">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>Toda cifra procede del <strong>Project Cost Ledger</strong> (fuente de verdad única) y permite drill-down hasta sus movimientos origen.</span>
        <span className={`ts-chip ${e.controls_open ? "ts-chip-danger" : "ts-chip-success"}`} data-testid="econ-controls-open">
          {e.controls_open ? `${e.controls_open} controles con hallazgos` : "Controles anti doble conteo: sin hallazgos"}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {figures.map((f) => (
          <Link key={f.key} to={`/proyectos/economia?project=${pid}&tab=${f.drill}${f.params ? `&${f.params}` : ""}`}
            className="ts-card block p-4 transition-all hover:border-blue-300 hover:shadow-sm" data-testid={f.testid}>
            <p className="ts-caption">{f.label}</p>
            <p className={`mt-1 font-mono text-xl font-bold ${f.key === "variance" ? (f.value < 0 ? "text-red-600" : "text-emerald-700") : "text-slate-900"}`}>
              {fmtEUR(f.value)}
            </p>
            <p className="mt-1 text-xs text-blue-700">Ver movimientos origen →</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------------- ESTRUCTURA DE COSTES ----------------

function EstructuraTab({ pid }) {
  const { data } = useQuery({ queryKey: ["econ-estructura", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/estructura`)).data });
  if (!data) return null;
  // Agrupado visual: cada padre (05) muestra el subtotal consolidado de sus hijas (05.1–05.4)
  const children = {};
  for (const r of data.rows) if (r.parent_code) (children[r.parent_code] = children[r.parent_code] || []).push(r);
  const parents = data.rows.filter((r) => !r.parent_code);
  const ordered = [];
  for (const p of parents) {
    const kids = children[p.cost_code] || [];
    if (kids.length) {
      const agg = { cost_code: p.cost_code, is_rollup: true };
      for (const k of ["presupuesto", "compromiso", "certificado", "facturado", "pagado"]) {
        agg[k] = round2(kids.reduce((s, c) => s + (c[k] || 0), 0) + (p[k] || 0));
      }
      const fcKids = kids.filter((c) => c.forecast_final != null);
      agg.forecast_final = fcKids.length ? round2(fcKids.reduce((s, c) => s + c.forecast_final, 0) + (p.forecast_final || 0)) : p.forecast_final;
      agg.variance = agg.forecast_final != null ? round2(agg.presupuesto - agg.forecast_final) : null;
      ordered.push(agg, ...kids);
    } else {
      ordered.push(p);
    }
  }
  return (
    <div className="ts-card overflow-x-auto" data-testid="econ-estructura">
      <p className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        Códigos de coste jerárquicos: las filas de grupo consolidan a sus hijas (05 = 05.1+05.2+05.3+05.4). Forecast v{data.forecast_version ?? "—"}.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead><TableHead>Partida</TableHead>
            <TableHead className="text-right">Presupuesto</TableHead><TableHead className="text-right">Comprometido</TableHead>
            <TableHead className="text-right">Certificado</TableHead><TableHead className="text-right">Facturado</TableHead>
            <TableHead className="text-right">Pagado</TableHead><TableHead className="text-right">Forecast final</TableHead>
            <TableHead className="text-right">Varianza</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordered.map((r) => (
            <TableRow key={r.cost_code} data-testid={`cost-code-${r.cost_code.replace(/\./g, "-")}`} className={r.is_rollup ? "bg-slate-50/70 font-semibold" : ""}>
              <TableCell className="font-mono text-xs">{r.is_rollup ? "" : "  "}{r.cost_code}</TableCell>
              <TableCell className="text-xs">{COST_CODE_NAMES[r.cost_code] || "—"}{r.is_rollup ? " (total grupo)" : ""}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.presupuesto)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.compromiso)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.certificado)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.facturado)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.pagado)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtEUR(r.forecast_final)}</TableCell>
              <TableCell className={`text-right font-mono text-xs font-semibold ${(r.variance ?? 0) < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`variance-${r.cost_code.replace(/\./g, "-")}`}>
                {r.variance != null ? fmtEUR(r.variance) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ---------------- LEDGER (movimientos) ----------------

function MovimientosTab({ pid }) {
  const [params] = useSearchParams();
  const [stage, setStage] = useState(params.get("stage") || "");
  const [sourceType, setSourceType] = useState(params.get("source_type") || "");
  const { data: movs } = useQuery({
    queryKey: ["econ-movs", pid, stage, sourceType],
    queryFn: async () => (await api.get(`/proyectos/${pid}/economia/movimientos`, { params: { ...(stage && { stage }), ...(sourceType && { source_type: sourceType }) } })).data,
  });
  if (!movs) return null;
  const linkFor = (m) => {
    if (m.source_type === "contract") return `/legal/contratos/${m.source_id}`;
    if (m.source_type === "po") return `/finanzas/ordenes/${m.source_id}`;
    if (m.source_type === "invoice") return `/finanzas/facturas/${m.source_id}`;
    return null;
  };
  return (
    <div className="space-y-3" data-testid="econ-movimientos">
      <div className="ts-card flex flex-wrap items-center gap-2 p-3">
        <Select value={stage || "__all"} onValueChange={(v) => setStage(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="ledger-filter-stage"><SelectValue placeholder="Etapa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todas las etapas</SelectItem>
            {Object.entries(LEDGER_STAGE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceType || "__all"} onValueChange={(v) => setSourceType(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="ledger-filter-source"><SelectValue placeholder="Fuente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todas las fuentes</SelectItem>
            {Object.entries(LEDGER_SOURCE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto font-mono text-xs text-slate-400" data-testid="ledger-count">{movs.length} movimientos</span>
      </div>
      <div className="ts-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead><TableHead>Etapa</TableHead><TableHead>Fuente</TableHead>
              <TableHead>Compromiso padre</TableHead><TableHead>Código</TableHead>
              <TableHead className="text-right">Importe</TableHead><TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movs.length === 0 && <TableRow><TableCell colSpan={7} className="py-5 text-center text-sm text-slate-500">Sin movimientos para estos filtros.</TableCell></TableRow>}
            {movs.map((m) => {
              const link = linkFor(m);
              return (
                <TableRow key={m.id} data-testid={`ledger-row-${m.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(m.date)}</TableCell>
                  <TableCell><Chip map={LEDGER_STAGE} value={m.stage} /></TableCell>
                  <TableCell className="text-xs">
                    {link ? <Link to={link} className="text-blue-700 hover:underline" data-testid={`ledger-link-${m.id}`}>{LEDGER_SOURCE[m.source_type]}</Link> : LEDGER_SOURCE[m.source_type]}
                    <p className="text-slate-400">{m.number || m.certification_number || m.reference || m.title || m.source_id}</p>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{m.parent_commitment ? m.parent_commitment.slice(0, 24) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{m.cost_code || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{fmtEUR(m.amount)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{m.status}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- CAMBIOS ----------------

function CambiosTab({ pid, canEcon }) {
  const qc = useQueryClient();
  const { map } = useUsers();
  const [dialog, setDialog] = useState(null); // {change, action}
  const [form, setForm] = useState({});
  const [reasons, setReasons] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { data: items } = useQuery({ queryKey: ["econ-cambios", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/cambios`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["econ-cambios", pid] }); qc.invalidateQueries({ queryKey: ["econ", pid] }); };
  const act = async () => {
    setBusy(true);
    setReasons(null);
    try {
      const payload = { action: dialog.action };
      if (dialog.action === "analizar") Object.assign(payload, { impact_cost: Number(form.impact_cost), impact_days: Number(form.impact_days), contracts_affected: form.contracts_affected ? form.contracts_affected.split(",").map((s) => s.trim()).filter(Boolean) : [], risks: form.risks });
      if (dialog.action === "enviar_revision") payload.analysis = form.analysis;
      if (dialog.action === "incorporar") payload.confirm = !!form.confirm;
      if (dialog.action === "descartar") payload.motivo = form.motivo;
      await api.post(`/proyectos/${pid}/economia/cambios/${dialog.change.id}/accion`, payload);
      toast.success("Acción registrada con trazabilidad");
      setDialog(null);
      setForm({});
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const createChange = async () => {
    setBusy(true);
    try {
      await api.post(`/proyectos/${pid}/economia/cambios`, { title: form.title, description: form.description, cost_code: form.cost_code, origin: form.origin || "interno" });
      toast.success("Cambio solicitado");
      setCreateOpen(false);
      setForm({});
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!items) return null;
  const ACTION_LABELS = { analizar: "Analizar impacto", enviar_revision: "Enviar a revisión", solicitar_aprobacion: "Solicitar aprobación", incorporar: "Incorporar al presupuesto", descartar: "Rechazar" };
  return (
    <div className="space-y-3" data-testid="econ-cambios">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Flujo: solicitud → análisis de impacto (coste, plazo, contratos, riesgos) → revisión → aprobación → incorporación. Un cambio no aprobado nunca modifica Budget ni Planning.</p>
        {canEcon && <Button size="sm" onClick={() => { setCreateOpen(true); setForm({}); }} data-testid="change-create-open">Solicitar cambio</Button>}
      </div>
      {items.length === 0 && <EmptyState title="Sin cambios" description="No hay cambios de proyecto registrados." testid="changes-empty" />}
      {items.map((c) => (
        <div key={c.id} className="ts-card p-4" data-testid={`change-card-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-sm font-semibold">{c.title}</p>
              <Chip map={CHANGE_STATUS} value={c.status} testid={`change-status-${c.id}`} />
              {c.impact_cost != null && <span className="font-mono text-xs font-semibold text-slate-800">{fmtEUR(c.impact_cost)}</span>}
              {c.impact_days != null && <span className="font-mono text-xs text-slate-500">{c.impact_days} días</span>}
              {c.cost_code && <span className="font-mono text-xs text-slate-400">{c.cost_code}</span>}
            </div>
            <span className="text-xs text-slate-400">{userName(map, c.requested_by)} · {fmtDate(c.created_at)}</span>
          </div>
          {c.analysis && <p className="mt-1 text-xs text-slate-500">Análisis: {c.analysis}</p>}
          {c.risks && <p className="mt-0.5 text-xs text-slate-500">Riesgos: {c.risks}</p>}
          {c.contracts_affected?.length > 0 && <p className="mt-0.5 text-xs text-slate-500">Contratos afectados: {c.contracts_affected.length}</p>}
          {c.approved_by && <p className="mt-0.5 text-xs text-slate-400">Aprobado por {userName(map, c.approved_by)} el {fmtDate(c.approved_at)}{c.incorporated_at ? ` · incorporado el ${fmtDate(c.incorporated_at)}` : " · pendiente de incorporación"}</p>}
          {canEcon && c.actions?.length > 0 && (
            <div className="mt-2 space-y-1">
              <div className="flex flex-wrap gap-2">
                {c.actions.filter((a) => a.allowed).map((a) => (
                  <Button key={a.action} size="sm" variant={a.action === "incorporar" ? "default" : "outline"} className="h-7 text-xs"
                    onClick={() => { setDialog({ change: c, action: a.action }); setForm({}); setReasons(null); }}
                    data-testid={`change-${a.action}-${c.id}`}>
                    {ACTION_LABELS[a.action]}
                  </Button>
                ))}
              </div>
              {c.actions.filter((a) => !a.allowed && a.permission).map((a) => (
                <p key={a.action} className="text-xs text-slate-400" data-testid={`change-blocked-note-${c.id}-${a.action}`}>
                  {ACTION_LABELS[a.action]}: {a.reasons?.[0]?.message || "no disponible"}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="change-action-dialog">
          <DialogHeader><DialogTitle>{dialog ? ACTION_LABELS[dialog.action] : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dialog?.action === "analizar" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Impacto en coste (€) *</Label><Input type="number" value={form.impact_cost ?? ""} onChange={(e) => setForm({ ...form, impact_cost: e.target.value })} data-testid="change-impact-cost" /></div>
                  <div><Label>Impacto en plazo (días) *</Label><Input type="number" value={form.impact_days ?? ""} onChange={(e) => setForm({ ...form, impact_days: e.target.value })} data-testid="change-impact-days" /></div>
                </div>
                <div><Label>Contratos afectados (ids separados por coma)</Label><Input value={form.contracts_affected ?? ""} onChange={(e) => setForm({ ...form, contracts_affected: e.target.value })} data-testid="change-contracts" /></div>
                <div><Label>Riesgos</Label><Textarea value={form.risks ?? ""} onChange={(e) => setForm({ ...form, risks: e.target.value })} data-testid="change-risks" /></div>
              </>
            )}
            {dialog?.action === "enviar_revision" && <div><Label>Análisis de impacto completo *</Label><Textarea value={form.analysis ?? ""} onChange={(e) => setForm({ ...form, analysis: e.target.value })} data-testid="change-analysis" /></div>}
            {dialog?.action === "incorporar" && (
              <div className="space-y-2">
                <p className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Incorporar actualiza el presupuesto vigente vía ledger (histórico conservado){dialog.change.impact_days ? " y crea una nueva versión borrador del planning (sin tocar el vigente)" : ""}.</p>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })} data-testid="change-incorporate-confirm" />
                  Confirmo que he revisado el impacto en coste, plazo y contratos
                </label>
              </div>
            )}
            {dialog?.action === "descartar" && <div><Label>Motivo *</Label><Textarea value={form.motivo ?? ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="change-motivo" /></div>}
            {reasons && <ReasonsList reasons={reasons} testid="change-blocked" />}
            <Button className="w-full" disabled={busy} onClick={act} data-testid="change-action-submit">{busy ? "Procesando…" : "Confirmar"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="change-create-dialog">
          <DialogHeader><DialogTitle>Solicitar cambio de proyecto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="change-title" /></div>
            <div><Label>Descripción</Label><Textarea value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="change-description" /></div>
            <div><Label>Código de coste</Label><Input value={form.cost_code ?? ""} onChange={(e) => setForm({ ...form, cost_code: e.target.value })} placeholder="p. ej. 05.3" data-testid="change-cost-code" /></div>
            <Button className="w-full" disabled={busy || !form.title?.trim()} onClick={createChange} data-testid="change-create-submit">{busy ? "Registrando…" : "Solicitar cambio"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- FORECAST ----------------

function ForecastTab({ pid, canEcon }) {
  const qc = useQueryClient();
  const { map, users } = useUsers();
  const [explain, setExplain] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLines, setNewLines] = useState(null);
  const [reasons, setReasons] = useState(null);
  const { data } = useQuery({ queryKey: ["econ-forecast", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/forecast`)).data });
  const { data: estructura } = useQuery({ queryKey: ["econ-estructura", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/estructura`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["econ-forecast", pid] }); qc.invalidateQueries({ queryKey: ["econ", pid] }); };
  if (!data) return null;
  const openCreate = () => {
    // Hojas reales: códigos sin hijos propios (01,02,03,04,06,07 y las hijas 05.1–05.4)
    const codesWithChildren = new Set((estructura?.rows || []).filter((r) => r.parent_code).map((r) => r.parent_code));
    setNewLines((estructura?.rows || []).filter((r) => !codesWithChildren.has(r.cost_code)).map((r) => ({
      cost_code: r.cost_code, current_budget: r.presupuesto, certified: r.certificado,
      forecast_final: r.presupuesto, cause: "", source: "", comment: "", responsible: "",
    })));
    setReasons(null);
    setCreateOpen(true);
  };
  const createForecast = async () => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/proyectos/${pid}/economia/forecast`, {
        lines: newLines.map((l) => ({
          cost_code: l.cost_code, current_budget: l.current_budget, certified: l.certified,
          forecast_final: Number(l.forecast_final), to_complete: Number(l.forecast_final) - (l.certified || 0),
          cause: l.cause || null, source: l.source || null, responsible: l.responsible || null, comment: l.comment || null,
        })),
        notes: "Creado desde UI",
      });
      toast.success("Forecast creado en borrador — sigue el flujo de aprobación");
      setCreateOpen(false);
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const act = async (fid, action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/proyectos/${pid}/economia/forecast/${fid}/accion`, { action, ...extra });
      toast.success("Acción registrada");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doExplain = async (fid) => {
    setBusy(true);
    try {
      const r = await api.post(`/proyectos/${pid}/economia/forecast/${fid}/explicar`);
      setExplain(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const aprobada = data.versions.find((v) => v.status === "aprobada");
  const anterior = data.versions.find((v) => v.status === "sustituida");
  return (
    <div className="space-y-3" data-testid="econ-forecast">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Forecast versionado e inmutable una vez aprobado. Toda variación exige importe, causa, responsable, fuente y comentario.</p>
        {canEcon && <Button size="sm" onClick={openCreate} data-testid="forecast-create-open">Nuevo forecast</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-3" data-testid="forecast-compare">
        <div className="ts-card p-4"><p className="ts-caption">Budget vigente</p><p className="mt-1 font-mono text-xl font-bold">{fmtEUR(data.current_budget)}</p></div>
        <div className="ts-card p-4" data-testid="forecast-anterior"><p className="ts-caption">Forecast anterior {anterior ? `(v${anterior.number})` : ""}</p><p className="mt-1 font-mono text-xl font-bold text-slate-500">{anterior ? fmtEUR(anterior.total_final) : "—"}</p></div>
        <div className="ts-card p-4" data-testid="forecast-actual"><p className="ts-caption">Forecast actual {aprobada ? `(v${aprobada.number})` : ""}</p><p className="mt-1 font-mono text-xl font-bold">{aprobada ? fmtEUR(aprobada.total_final) : "—"}</p></div>
      </div>
      {data.versions.map((fv) => (
        <div key={fv.id} className="ts-card p-4" data-testid={`forecast-card-${fv.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold">Forecast v{fv.number}</span>
              <Chip map={FORECAST_V_STATUS} value={fv.status} testid={`forecast-status-${fv.id}`} />
              <span className="font-mono text-xs">{fmtEUR(fv.total_final)}</span>
              <span className={`font-mono text-xs font-semibold ${fv.variance_vs_budget < 0 ? "text-red-600" : "text-emerald-700"}`}>varianza {fmtEUR(fv.variance_vs_budget)}</span>
            </div>
            <span className="text-xs text-slate-400">{userName(map, fv.created_by)} · {fmtDate(fv.created_at)}{fv.approved_by ? ` · aprobada por ${userName(map, fv.approved_by)}` : ""}</span>
          </div>
          <div className="mt-3 overflow-x-auto rounded-sm border border-slate-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead><TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Forecast final</TableHead><TableHead className="text-right">Variación</TableHead>
                  <TableHead>Causa</TableHead><TableHead>Responsable</TableHead><TableHead>Fuente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fv.lines.map((l) => {
                  const variation = (l.forecast_final || 0) - (l.current_budget || 0);
                  return (
                    <TableRow key={l.cost_code} data-testid={`forecast-line-${fv.id}-${l.cost_code.replace(/\./g, "-")}`} className={Math.abs(variation) > 0.01 ? "bg-amber-50/40" : ""}>
                      <TableCell className="font-mono text-xs">{l.cost_code}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtEUR(l.current_budget)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtEUR(l.forecast_final)}</TableCell>
                      <TableCell className={`text-right font-mono text-xs font-semibold ${variation > 0 ? "text-red-600" : variation < 0 ? "text-emerald-700" : "text-slate-400"}`}>{variation ? fmtEUR(variation) : "—"}</TableCell>
                      <TableCell className="max-w-56 text-xs text-slate-600">{l.cause || "—"}</TableCell>
                      <TableCell className="text-xs">{l.responsible ? userName(map, l.responsible) : "—"}</TableCell>
                      <TableCell className="max-w-44 text-xs text-slate-500">{l.source || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {canEcon && (
            <div className="mt-2 flex flex-wrap gap-2">
              {fv.actions?.filter((a) => a.allowed).map((a) => (
                <Button key={a.action} size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => act(fv.id, a.action, a.action === "descartar" ? { motivo: "Descartado desde UI" } : {})}
                  disabled={busy} data-testid={`forecast-${a.action}-${fv.id}`}>
                  {a.action === "solicitar_aprobacion" ? "Solicitar aprobación" : "Descartar"}
                </Button>
              ))}
              {canEcon && fv.status !== "sustituida" && fv.status !== "descartada" && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doExplain(fv.id)} disabled={busy} data-testid={`forecast-explain-${fv.id}`}>
                  Explicar desviación (IA)
                </Button>
              )}
            </div>
          )}
          {fv.ai_explanation && (
            <div className="mt-2 rounded-sm border border-violet-200 bg-violet-50 p-3" data-testid={`forecast-ai-${fv.id}`}>
              <p className="whitespace-pre-wrap text-xs text-violet-900">{fv.ai_explanation}</p>
            </div>
          )}
        </div>
      ))}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl" data-testid="forecast-create-dialog">
          <DialogHeader><DialogTitle>Nuevo forecast (borrador)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Edita el coste final previsto por código. Toda variación respecto al presupuesto exige causa, responsable, fuente y comentario — el backend lo valida y bloquea si falta.</p>
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {(newLines || []).map((l, i) => {
                const varies = Math.abs(Number(l.forecast_final || 0) - l.current_budget) > 0.01;
                return (
                  <div key={l.cost_code} className={`rounded-sm border p-2 ${varies ? "border-amber-300 bg-amber-50/40" : "border-slate-100"}`} data-testid={`forecast-new-line-${l.cost_code.replace(/\./g, "-")}`}>
                    <div className="grid grid-cols-[70px_130px_1fr_1fr_130px] items-center gap-1">
                      <span className="font-mono text-xs">{l.cost_code}</span>
                      <span className="font-mono text-xs text-slate-400">{fmtEUR(l.current_budget)}</span>
                      <Input type="number" value={l.forecast_final} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, forecast_final: e.target.value } : x))} data-testid={`forecast-new-final-${l.cost_code.replace(/\./g, "-")}`} />
                      <Input value={l.cause} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, cause: e.target.value } : x))} placeholder={varies ? "Causa *" : "Causa"} data-testid={`forecast-new-cause-${l.cost_code.replace(/\./g, "-")}`} />
                      <Select value={l.responsible || "__none"} onValueChange={(v) => setNewLines(newLines.map((x, j) => j === i ? { ...x, responsible: v === "__none" ? "" : v } : x))}>
                        <SelectTrigger data-testid={`forecast-new-responsible-${l.cost_code.replace(/\./g, "-")}`}><SelectValue placeholder={varies ? "Responsable *" : "Responsable"} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">—</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {varies && (
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        <Input value={l.source} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, source: e.target.value } : x))} placeholder="Fuente *" data-testid={`forecast-new-source-${l.cost_code.replace(/\./g, "-")}`} />
                        <Input value={l.comment} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, comment: e.target.value } : x))} placeholder="Comentario *" data-testid={`forecast-new-comment-${l.cost_code.replace(/\./g, "-")}`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {reasons && <ReasonsList reasons={reasons} testid="forecast-create-blocked" />}
            <Button className="w-full" disabled={busy} onClick={createForecast} data-testid="forecast-create-submit">{busy ? "Creando…" : "Crear forecast en borrador"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!explain} onOpenChange={() => setExplain(null)}>
        <DialogContent data-testid="forecast-explain-dialog">
          <DialogHeader><DialogTitle>Explicación de la desviación (IA demo)</DialogTitle></DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{explain?.explanation}</p>
          <p className="mt-2 rounded-sm border border-violet-200 bg-violet-50 p-2 text-xs text-violet-800">{explain?.disclaimer}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- CERTIFICACIONES ----------------

const CERT_ACTION_LABELS = { extraer: "Extraer (IA)", validar: "Validar", revision_tecnica: "Revisión técnica", revision_economica: "Revisión económica", solicitar_aprobacion: "Solicitar aprobación", rechazar: "Rechazar" };

function CertificacionesTab({ pid, canEcon }) {
  const qc = useQueryClient();
  const { map } = useUsers();
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState(null); // {cert, action}
  const [form, setForm] = useState({});
  const [decisions, setDecisions] = useState({});
  const [reasons, setReasons] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLines, setNewLines] = useState([{ cost_code: "", description: "", requested: "", evidence: "" }]);
  const { data } = useQuery({ queryKey: ["econ-certs", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/certificaciones`)).data });
  const { data: det } = useQuery({
    queryKey: ["econ-cert", pid, detail],
    queryFn: async () => (await api.get(`/proyectos/${pid}/economia/certificaciones/${detail}`)).data,
    enabled: !!detail,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["econ-certs", pid] }); qc.invalidateQueries({ queryKey: ["econ-cert", pid] }); qc.invalidateQueries({ queryKey: ["econ", pid] }); };
  if (!data) return null;
  const createCert = async () => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/proyectos/${pid}/economia/certificaciones`, {
        contract_id: form.contract_id, period: form.period,
        lines: newLines.filter((l) => l.cost_code && l.requested).map((l) => ({
          cost_code: l.cost_code, description: l.description || `Partida ${l.cost_code}`,
          requested: Number(l.requested), evidence_required: true, evidence: l.evidence || null,
        })),
      });
      toast.success("Certificación recibida — inicia el flujo de validación y revisiones");
      setCreateOpen(false);
      setForm({});
      setNewLines([{ cost_code: "", description: "", requested: "", evidence: "" }]);
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const act = async () => {
    setBusy(true);
    setReasons(null);
    try {
      const payload = { action: dialog.action };
      if (dialog.action === "revision_tecnica") payload.technical_comment = form.technical_comment;
      if (dialog.action === "revision_economica") payload.economic_comment = form.economic_comment;
      if (dialog.action === "rechazar") payload.motivo = form.motivo;
      await api.post(`/proyectos/${pid}/economia/certificaciones/${dialog.cert.id}/accion`, payload);
      toast.success("Acción registrada");
      setDialog(null);
      setForm({});
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const submitLines = async () => {
    setBusy(true);
    setReasons(null);
    try {
      const ds = Object.entries(decisions).map(([cost_code, d]) => ({ cost_code, decision: d.decision, amount: d.amount ? Number(d.amount) : undefined }));
      await api.post(`/proyectos/${pid}/economia/certificaciones/${detail}/lineas`, { decisions: ds });
      toast.success("Certificación aprobada por partidas — economía actualizada vía ledger");
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="econ-certificaciones">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">Flujo: recepción → extracción → validación → revisión técnica → revisión económica → comparación con contrato → aprobación (parcial por partidas: solicitado / aprobado / rechazado / retenido) → actualización económica → elegibilidad para facturación.</p>
        {canEcon && data.contracts.length > 0 && (
          <Button size="sm" onClick={() => { setCreateOpen(true); setForm({}); setReasons(null); }} data-testid="cert-create-open">Nueva certificación</Button>
        )}
      </div>
      {data.certifications.length === 0 && <EmptyState title="Sin certificaciones" description="Aún no hay certificaciones de obra en este proyecto." testid="certs-empty" />}
      {data.certifications.map((c) => (
        <div key={c.id} className="ts-card p-4" data-testid={`cert-card-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-sm font-semibold">{c.number}</p>
              <Chip map={CERT_STATUS} value={c.status} testid={`cert-status-${c.id}`} />
              <span className="font-mono text-xs">solicitado {fmtEUR(c.amount_requested)}</span>
              {c.amount_approved != null && <span className="font-mono text-xs text-emerald-700">aprobado {fmtEUR(c.amount_approved)}</span>}
              {(c.amount_retained ?? 0) > 0 && <span className="font-mono text-xs text-amber-700">retenido {fmtEUR(c.amount_retained)}</span>}
              {c.eligible_for_billing && <Chip map={{ true: { label: "Elegible para facturación", tone: "success" } }} value={String(c.eligible_for_billing)} fallback={c.eligible_for_billing} testid={`cert-billing-${c.id}`} />}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">{c.period} · {c.origin_document}</span>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setDetail(detail === c.id ? null : c.id); setDecisions({}); setReasons(null); }} data-testid={`cert-detail-${c.id}`}>
                {detail === c.id ? "Ocultar" : "Detalle"}
              </Button>
            </div>
          </div>
          {detail === c.id && det && (
            <div className="mt-3 space-y-3 border-t border-slate-100 pt-3" data-testid={`cert-open-${c.id}`}>
              {det.blockers.length > 0 && (
                <div data-testid={`cert-blockers-${c.id}`}>
                  <p className="mb-1 text-xs font-semibold text-red-800">Bloqueos de aprobación:</p>
                  <ReasonsList reasons={det.blockers} testid={`cert-blocker-list-${c.id}`} />
                </div>
              )}
              <div className="overflow-x-auto rounded-sm border border-slate-100">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead><TableHead>Partida</TableHead>
                      <TableHead className="text-right">Solicitado</TableHead><TableHead className="text-right">Aprobado</TableHead>
                      <TableHead className="text-right">Rechazado</TableHead><TableHead className="text-right">Retenido</TableHead>
                      <TableHead>Evidencia</TableHead><TableHead>Estado</TableHead>
                      {det.can_decide_lines && <TableHead>Decisión</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {det.certification.lines.map((l) => (
                      <TableRow key={l.cost_code} data-testid={`cert-line-${c.id}-${l.cost_code.replace(/\./g, "-")}`}>
                        <TableCell className="font-mono text-xs">{l.cost_code}</TableCell>
                        <TableCell className="max-w-52 text-xs">{l.description}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtEUR(l.requested)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-700">{l.approved != null ? fmtEUR(l.approved) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-red-600">{l.rejected ? fmtEUR(l.rejected) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-amber-700">{l.retained ? fmtEUR(l.retained) : "—"}</TableCell>
                        <TableCell className="text-xs">{l.evidence_required ? (l.evidence ? `✓ ${l.evidence}` : "✗ falta") : "no requerida"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{l.status}</TableCell>
                        {det.can_decide_lines && (
                          <TableCell>
                            <div className="flex gap-1">
                              {["aprobar", "retener", "rechazar"].map((d) => (
                                <Button key={d} size="sm" variant={decisions[l.cost_code]?.decision === d ? "default" : "outline"} className="h-6 px-2 text-[11px]"
                                  onClick={() => setDecisions({ ...decisions, [l.cost_code]: { decision: d, amount: d === "retener" ? Math.round(l.requested * 0.1) : undefined } })}
                                  data-testid={`cert-decide-${c.id}-${l.cost_code.replace(/\./g, "-")}-${d}`}>
                                  {d}
                                </Button>
                              ))}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {det.certification.technical_review && <p className="text-xs text-slate-500">Revisión técnica: {det.certification.technical_review.comment} ({userName(map, det.certification.technical_review.by)})</p>}
              {det.certification.economic_review && <p className="text-xs text-slate-500">Revisión económica: {det.certification.economic_review.comment} ({userName(map, det.certification.economic_review.by)})</p>}
              {det.can_decide_lines && Object.keys(decisions).length > 0 && (
                <div>
                  <Button size="sm" onClick={submitLines} disabled={busy} data-testid={`cert-decide-submit-${c.id}`}>{busy ? "Aprobando…" : "Aprobar con estas decisiones de partidas"}</Button>
                  {reasons && <div className="mt-2"><ReasonsList reasons={reasons} testid={`cert-lines-blocked-${c.id}`} /></div>}
                </div>
              )}
              {canEcon && det.actions?.filter((a) => a.allowed).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {det.actions.filter((a) => a.allowed).map((a) => (
                    <Button key={a.action} size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => { setDialog({ cert: c, action: a.action }); setForm({}); setReasons(null); }}
                      data-testid={`cert-${a.action}-${c.id}`}>
                      {CERT_ACTION_LABELS[a.action]}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="cert-create-dialog">
          <DialogHeader><DialogTitle>Nueva certificación de obra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Contrato *</Label>
              <Select value={form.contract_id || ""} onValueChange={(v) => setForm({ ...form, contract_id: v })}>
                <SelectTrigger data-testid="cert-create-contract"><SelectValue placeholder="Selecciona contrato…" /></SelectTrigger>
                <SelectContent>
                  {data.contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`cert-contract-opt-${c.id}`}>
                      {c.contract_number} — saldo {fmtEUR((c.total_value || 0) - (c.certified_total || 0))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Periodo *</Label><Input type="month" value={form.period ?? ""} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="cert-create-period" /></div>
            <div className="space-y-2">
              <Label>Partidas (código de coste · descripción · importe solicitado · evidencia)</Label>
              {newLines.map((l, i) => (
                <div key={i} className="grid grid-cols-[90px_1fr_110px_1fr] items-center gap-1" data-testid={`cert-new-line-${i}`}>
                  <Input value={l.cost_code} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, cost_code: e.target.value } : x))} placeholder="05.1" data-testid={`cert-new-line-code-${i}`} />
                  <Input value={l.description} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Descripción" />
                  <Input type="number" value={l.requested} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, requested: e.target.value } : x))} placeholder="€" data-testid={`cert-new-line-amount-${i}`} />
                  <Input value={l.evidence} onChange={(e) => setNewLines(newLines.map((x, j) => j === i ? { ...x, evidence: e.target.value } : x))} placeholder="Evidencia / mediciones" data-testid={`cert-new-line-evidence-${i}`} />
                </div>
              ))}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setNewLines([...newLines, { cost_code: "", description: "", requested: "", evidence: "" }])} data-testid="cert-add-line">+ Añadir partida</Button>
            </div>
            {reasons && <ReasonsList reasons={reasons} testid="cert-create-blocked" />}
            <Button className="w-full" disabled={busy || !form.contract_id || !form.period || !newLines.some((l) => l.cost_code && l.requested)} onClick={createCert} data-testid="cert-create-submit">
              {busy ? "Registrando…" : "Registrar certificación"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="cert-action-dialog">
          <DialogHeader><DialogTitle>{dialog ? CERT_ACTION_LABELS[dialog.action] : ""} — {dialog?.cert.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {dialog?.action === "revision_tecnica" && <div><Label>Comentario de revisión técnica *</Label><Textarea value={form.technical_comment ?? ""} onChange={(e) => setForm({ ...form, technical_comment: e.target.value })} data-testid="cert-technical-comment" /></div>}
            {dialog?.action === "revision_economica" && <div><Label>Comentario de revisión económica *</Label><Textarea value={form.economic_comment ?? ""} onChange={(e) => setForm({ ...form, economic_comment: e.target.value })} data-testid="cert-economic-comment" /></div>}
            {dialog?.action === "rechazar" && <div><Label>Motivo *</Label><Textarea value={form.motivo ?? ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="cert-motivo" /></div>}
            {["extraer", "validar", "solicitar_aprobacion"].includes(dialog?.action) && <p className="text-xs text-slate-500">La acción queda registrada con trazabilidad (usuario, fecha, estado anterior y nuevo).</p>}
            {reasons && <ReasonsList reasons={reasons} testid="cert-action-blocked" />}
            <Button className="w-full" disabled={busy} onClick={act} data-testid="cert-action-submit">{busy ? "Procesando…" : "Confirmar"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- PROGRESO FÍSICO VS FINANCIERO ----------------

function ProgresoTab({ pid }) {
  const { data } = useQuery({ queryKey: ["econ-progreso", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/progreso`)).data });
  const { data: controles } = useQuery({ queryKey: ["econ-controles", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/economia/controles`)).data });
  if (!data || !controles) return null;
  const bars = [
    { label: "Physical Progress (avance físico)", value: data.physical_progress, tone: "bg-blue-600", testid: "progress-physical" },
    { label: "Planned Progress (planificado)", value: data.planned_progress, tone: "bg-slate-400", testid: "progress-planned" },
    { label: "Certified Progress", value: data.certified_progress, tone: "bg-emerald-600", testid: "progress-certified" },
    { label: "Invoiced Progress", value: data.invoiced_progress, tone: "bg-indigo-500", testid: "progress-invoiced" },
    { label: "Paid Progress", value: data.paid_progress, tone: "bg-teal-600", testid: "progress-paid" },
  ];
  return (
    <div className="space-y-4" data-testid="econ-progreso">
      <div className="ts-card space-y-3 p-4" data-testid="progress-bars">
        {bars.map((b) => (
          <div key={b.testid}>
            <div className="mb-1 flex justify-between text-xs">
              <span className="text-slate-600">{b.label}</span>
              <span className="font-mono font-semibold" data-testid={b.testid}>{b.value != null ? `${b.value}%` : "—"}</span>
            </div>
            <div className="h-2.5 rounded-sm bg-slate-100">
              <div className={`h-2.5 rounded-sm transition-[width] ${b.tone}`} style={{ width: `${Math.min(100, b.value || 0)}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2" data-testid="progress-detections">
        <p className="ts-caption">Detecciones automáticas avance vs economía</p>
        {data.detections.length === 0 && <p className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="detections-clean">Sin desalineamientos: certificación, facturación y pago son coherentes con el avance físico.</p>}
        {data.detections.map((d, i) => (
          <div key={i} className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid={`detection-${d.code}`}>
            <p className="text-sm font-medium text-red-900">{d.message}</p>
            <p className="text-xs text-red-700">Severidad: {d.severity}</p>
          </div>
        ))}
      </div>

      <div className="space-y-2" data-testid="controls-list">
        <p className="ts-caption">Controles anti doble conteo</p>
        {controles.length === 0 && <p className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="controls-clean">Los 6 controles automáticos (contrato+PO duplicado, factura ya contabilizada, certificación/factura duplicadas, PO fuera de contrato, factura sin compromiso, sobrecertificación) no muestran hallazgos.</p>}
        {controles.map((f, i) => (
          <div key={i} className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid={`control-${f.control}`}>
            <p className="text-sm font-medium text-red-900">{f.message}</p>
            <p className="text-xs text-red-700">{f.control} · severidad {f.severity} · origen {f.source_type}/{f.source_id}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- PÁGINA ----------------

export default function Economia() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const pid = params.get("project");
  const tab = params.get("tab") || "resumen";
  const { data: projects } = useQuery({ queryKey: ["proyectos"], queryFn: async () => (await api.get("/proyectos")).data });
  const { data: summary } = useQuery({
    queryKey: ["econ", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/economia`)).data,
    enabled: !!pid,
  });
  const setTab = (t) => setParams({ project: pid, tab: t });
  const project = projects?.find((p) => p.id === pid);
  const canEcon = !!user && !!project && (
    (user.roles || []).some((r) => ["PROCESS_OWNER", "AREA_MANAGER", "DEMO_ADMIN"].includes(r)) ||
    [project.pm_id, project.cost_manager_id, project.director_id].includes(user.id)
  );

  return (
    <div data-testid="economia-page" className="min-w-0 overflow-x-hidden">
      <PageHeader title="Control económico y certificaciones"
        subtitle="Project Cost Ledger como fuente de verdad única: contratos, POs, certificaciones, facturas y pagos son etapas del mismo compromiso — nunca se suman entre sí."
        actions={
          <Select value={pid || ""} onValueChange={(v) => setParams({ project: v, tab })}>
            <SelectTrigger className="w-72" data-testid="econ-project-selector"><SelectValue placeholder="Selecciona proyecto…" /></SelectTrigger>
            <SelectContent>
              {(projects || []).map((p) => <SelectItem key={p.id} value={p.id} data-testid={`econ-project-opt-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        } />

      {!pid && <EmptyState title="Selecciona un proyecto" description="Elige un proyecto para ver su control económico, ledger, cambios, forecast y certificaciones." testid="econ-no-project" />}

      {pid && user?.is_contractor && (
        <EmptyState title="Sin acceso a la economía" description="Los contratistas no tienen acceso al control económico del proyecto. Consulta tus certificaciones y facturas desde el portal de proveedor." testid="econ-contractor-blocked" />
      )}

      {pid && !user?.is_contractor && project && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="econ-header">
            <Link to={`/proyectos/${pid}`} className="text-sm font-medium text-blue-700 hover:underline" data-testid="econ-project-link">{project.name}</Link>
            {summary && <span className="ts-chip ts-chip-info" data-testid="econ-header-budget">Budget {fmtEUR(summary.current_budget)}</span>}
            {summary && <span className="ts-chip ts-chip-warning" data-testid="econ-header-committed">Committed {fmtEUR(summary.committed)}</span>}
            {summary && <span className="ts-chip ts-chip-success" data-testid="econ-header-certified">Certified {fmtEUR(summary.certified)}</span>}
            {summary && summary.variance != null && (
              <span className={`ts-chip ${summary.variance < 0 ? "ts-chip-danger" : "ts-chip-success"}`} data-testid="econ-header-variance">Varianza {fmtEUR(summary.variance)}</span>
            )}
            {summary && summary.controls_open > 0 && <span className="ts-chip ts-chip-danger" data-testid="econ-header-controls">{summary.controls_open} hallazgos de control</span>}
          </div>

          <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`econ-tab-${t.id}`}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "resumen" && <ResumenTab pid={pid} />}
          {tab === "estructura" && <EstructuraTab pid={pid} />}
          {tab === "movimientos" && <MovimientosTab pid={pid} />}
          {tab === "cambios" && <CambiosTab pid={pid} canEcon={canEcon} />}
          {tab === "forecast" && <ForecastTab pid={pid} canEcon={canEcon} />}
          {tab === "certificaciones" && <CertificacionesTab pid={pid} canEcon={canEcon} />}
          {tab === "progreso" && <ProgresoTab pid={pid} />}
        </>
      )}
      {pid && projects && !project && <EmptyState title="Proyecto no accesible" description="No existe o es confidencial y no perteneces a su equipo." testid="econ-no-access" />}
    </div>
  );
}
